import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { DEFAULT_REPORT_STATUS, type ReportStatusFilter } from '@/lib/reportStatus';
import { DEFAULT_STATEMENT_CATEGORY_LABEL, type StatementCategoryLabel } from '@/lib/statementCategoryLabel';
import { CALENDAR_MONTH_START_DAY, normalizeMonthStartDay } from '@/lib/monthCycle';

export type CurrencyCode = 'BRL' | 'USD' | 'EUR';
export type DateFormatOption = 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd';
export type LanguagePreference = 'system' | 'pt-BR' | 'en';

/** Compartilhada com lib/i18n.ts, que lê o JSON cru no boot pra saber se o idioma já foi decidido. */
export const PREFERENCES_STORAGE_KEY = '@finapp_preferences';

export interface PreferencesState {
    currency: CurrencyCode;
    dateFormat: DateFormatOption;
    /**
     * Idioma dos textos do app (ADR 0044). 'system' segue o dispositivo. Instalações
     * pré-i18n são cravadas em 'pt-BR' no boot (initAppLanguage em lib/i18n.ts) —
     * quem já usa o app em português continua nele mesmo com o celular em inglês.
     * Quem APLICA a troca no i18next é o subscribe em lib/i18n.ts, não este store.
     */
    language: LanguagePreference;
    /**
     * Início do mês (ADR 0190): o dia em que o "mês" das telas começa — 1 a 31; 1 é o
     * mês-calendário de sempre. Home, Transações, Análise, relatórios, widgets e projeção
     * passam a contar do dia escolhido até a véspera dele no mês seguinte ("20 de out – 19 de
     * nov"). Recurso PRO: sem Pro o app LÊ 1 e deixa o valor gravado (precedente do tema,
     * ADR 0026). Por isso NINGUÉM lê esta chave direto — lê `lib/monthStart.ts`, que aplica o
     * gate. Por aparelho, como toda preferência: não entra no backup nem no sync.
     *
     * Antecedente: `firstDayOfMonth` (1–28) morou aqui até 18/09/2026 sem tela nem leitor e
     * foi arrancado (config sem tela que a escreva não existe). Esta é a feature que faltava,
     * com nome novo de propósito — o valor antigo no AsyncStorage segue como chave a mais,
     * ignorada. Guarda do antecedente: lib/__tests__/deadConfig.test.ts.
     */
    monthStartDay: number;
    /** Mostra cada compra do cartão na lista, em vez de agrupar na fatura. */
    separateCardPurchases: boolean;
    /** Mostra só transações efetivadas (confirmadas) — esconde pendentes e projeções. */
    onlyConfirmedTransactions: boolean;
    /** Mostra as tags de cada transação como chips no extrato. Desligado por padrão. */
    showTagsInStatement: boolean;
    /** Mostra a observação/nota de cada transação no extrato. Desligado por padrão. */
    showNotesInStatement: boolean;
    /**
     * O que vai no subtítulo de cada lançamento nas listas: categoria (padrão), só a
     * subcategoria ou as duas. Vale em toda lista que usa o TransactionListItem. Ver
     * lib/statementCategoryLabel.ts.
     */
    statementCategoryLabel: StatementCategoryLabel;
    /**
     * Hora nos lançamentos: mostra o campo de hora no formulário e a hora nas
     * listas (extrato, recentes, fatura). LIGADO por padrão — a hora sempre foi
     * GRAVADA (captura/fallback) e ordena o intra-dia; agora ela também aparece
     * por padrão. Desligar só esconde da tela, nunca deixa de gravar.
     */
    showTransactionTime: boolean;
    /**
     * Inclui as observações na busca por texto das Transações. Ligado por padrão
     * (busca em descrição + observações); desligado = só na descrição (nome), pra
     * quem não quer que a observação de um lançamento polua a busca rápida.
     */
    searchInNotes: boolean;
    /**
     * Mostra o Saldo do período (Saída/Entrada/Saldo) no topo das Transações,
     * espelhando o recorte na tela (mês ou busca). WYSIWYG, transferências neutras.
     * Desligado por padrão. Ver CONTEXT.md ("Saldo do período") e docs/IDEAS.md #10.
     * Quando ligado, a lista também mostra o subtotal por dia no cabeçalho do dia.
     */
    showPeriodBalance: boolean;
    /**
     * Mantém as transações pendentes/projetadas destacadas no topo da lista (com
     * divisor "Concluídas"). Ligado por padrão = comportamento histórico. Desligado
     * = lista puramente cronológica por dia (combina com o subtotal por dia).
     */
    pendingOnTop: boolean;
    /**
     * Atrasadas no mês atual (ADR 0151, pedido do Edson): pendentes de meses anteriores
     * reaparecem no mês atual — seção "Atrasadas" no topo das Transações e somadas em Pagar e
     * Receber nas Pendências da Home (o Previsto das contas já as descontava). DESLIGADO
     * por padrão: quem não usa pendente como lembrete estranharia um agosto em setembro.
     */
    overdueInCurrentMonth: boolean;
    /**
     * Saldo acumulado por dia no extrato (pedido de usuário — migrante do Minhas
     * Finanças): cabeçalho de cada dia mostra o DISPONÍVEL das contas incluídas
     * (saldo total − reservado das caixinhas) ao FIM daquele dia, como extrato
     * bancário — o mesmo número que o app exibe no topo da Home e nas contas. É
     * POSIÇÃO (não fluxo): calculado dos impactos confirmados de conta menos o
     * reservado por dia, independente das linhas visíveis — e por isso some quando
     * há busca/filtro/segmento de tipo (o número não reflete o recorte). Com
     * "Pendentes em destaque", a seção Concluídas mostra o saldo confirmado e a de
     * Pendentes mostra o Disponível PREVISTO por dia (confirmado herdado + pendentes/
     * faturas/recorrências projetadas do mês); sem, só o confirmado. Desligado por padrão.
     */
    showDailyRunningBalance: boolean;
    /**
     * Sub-opção do saldo acumulado: ligado (padrão), o acumulado parte do saldo
     * herdado (saldo inicial das contas + tudo confirmado antes do mês) — semântica
     * de banco. Desligado, parte de R$ 0 no dia 1º e vira "quanto o mês rendeu até
     * aquele dia".
     */
    runningBalanceIncludesOpening: boolean;
    /**
     * "Considerar não efetivadas" do calendário de fluxo de caixa (chip da grade):
     * ligado, as células somam pendentes, faturas em aberto e recorrências projetadas.
     * Ligado por padrão (default histórico do chip) e agora PERSISTIDO — antes era
     * estado da tela e resetava a cada sessão. Só o calendário lê; no extrato, o
     * acumulado segue "Apenas efetivadas" (onlyConfirmedTransactions).
     */
    includePendingInBalances: boolean;
    /**
     * Mostra os movimentos de guardar/resgatar das caixinhas no extrato da CONTA
     * (pedido de usuário — Kawasaki). Linhas puramente informativas: não são
     * transações, não somam em receita/despesa nem entram em filtros. A aba
     * Transações fica de fora. Desligado por padrão. Ver ADR 0015.
     */
    showReservaMovementsInStatement: boolean;
    /**
     * Ordenação do extrato (Transações), persistida entre sessões (pedido de
     * usuário). Só o sort é pegajoso — os demais filtros do sheet continuam
     * efêmeros por sessão para não prender o usuário num recorte antigo.
     */
    statementSortBy: 'date' | 'amount' | 'category';
    statementSortDir: 'asc' | 'desc';
    /**
     * Ordem do extrato de CADA conta (tela da conta), pegajosa entre visitas e comum a
     * todas as contas. Separada do `statementSortDir` de Transações de propósito: lá o
     * padrão é decrescente (extrato de banco); aqui o extrato do mês nasce do dia 1 e
     * quem prefere o dia 31 no topo tocava na seta a cada visita (pedido de usuário —
     * Vitor e Josinei, set/2026). Só a seta muda isso; não tem toggle em Preferências.
     */
    accountStatementSortDir: 'asc' | 'desc';
    /**
     * Mostra "Caixinha" (guardar dinheiro) no menu radial do "+". Pedido de
     * usuário; atrás de config e desligado por padrão pra não poluir o menu.
     */
    showPocketQuickAction: boolean;
    /**
     * Soma o saldo em aberto de "Quem me deve" ao saldo total exibido na Home.
     * Desligado por padrão: dinheiro emprestado não é dinheiro disponível.
     */
    includeReceivablesInBalance: boolean;
    /**
     * Na Home, ao navegar para um mês futuro, mostra o resultado isolado daquele
     * mês (receitas − despesas do mês) em vez do saldo acumulado projetado.
     * Desligado por padrão: o comportamento de hoje segue o saldo previsto
     * acumulado. Serve para responder "quanto ainda posso gastar neste mês".
     */
    standaloneMonthlyForecast: boolean;
    /**
     * Sub-opção de `standaloneMonthlyForecast`: estende o resultado isolado do mês ao mês
     * ATUAL, onde a manchete mostra o saldo real. Desligada por padrão — quem não liga
     * segue com "Saldo total" no topo. Quem liga troca "quanto tenho agora" por "como
     * está o meu mês", e aí a compra no cartão passa a mexer no número (ADR 0136).
     */
    monthResultInCurrentMonth: boolean;
    /**
     * Parcelamento pelo valor total: nas leituras ANALÍTICAS em competência
     * (Balanço do mês, Análise, relatórios, orçamentos), a compra parcelada conta
     * UMA vez, pelo valor total, no mês da compra — em vez de uma parcela por mês.
     * Fatura, extrato, saldo e regime de caixa não mudam. Desligado por padrão
     * (comportamento histórico). Ver CONTEXT.md e ADR 0043.
     */
    installmentsFullValue: boolean;
    /**
     * Cartão no mês de fechamento: nas leituras ANALÍTICAS em competência, a fatura
     * pesa no mês em que FECHA (referenceMonth) em vez do mês do vencimento. Pedido
     * de usuário com cartão vencendo dia 01 — pelo vencimento, o mês inteiro de
     * compras desliza pro mês seguinte. Fatura, extrato, saldo, carrossel de cartões
     * da Home e regime de caixa não mudam. Desligado por padrão (comportamento
     * histórico). Ver CONTEXT.md e ADR 0050.
     */
    invoicesByClosingMonth: boolean;
    /**
     * Situação do **Fluxo de caixa**: `all` (padrão) conta pendentes e Ocorrências
     * projetadas junto com as concluídas; `settled` só as concluídas; `pending` só o que
     * falta acontecer. Padrão `all` porque ali a pergunta é "vou honrar meus compromissos?"
     * e o seletor de período aceita futuro — sem os previstos, os meses à frente vinham
     * zerados em silêncio. Ver CONTEXT.md e ADR 0063.
     */
    reportsStatus: ReportStatusFilter;
    /**
     * Situação do **Relatório personalizado** (e do PDF em Exportar, que é o mesmo dado).
     * Padrão `settled` = comportamento histórico da tela, que sempre foi só-confirmadas:
     * é uma ferramenta de análise de uso geral, não a tela do "vou conseguir pagar?", então
     * quem quiser o previsto pede. Chave separada do Fluxo de caixa de propósito — os dois
     * relatórios respondem perguntas diferentes e cada um lembra a sua escolha.
     */
    customReportStatus: ReportStatusFilter;
    /**
     * Mostra o total reservado (caixinhas) embaixo de cada conta na Home e no rodapé da seção
     * de Contas. Ligado por padrão. Afeta SÓ a Home — a tela de Contas mostra o reservado sempre.
     */
    showAccountReserved: boolean;
    /**
     * Teclado de cálculo no campo de valor do lançamento (despesa, receita e
     * despesa no cartão): sobe como sheet com + − × ÷ no lugar do teclado
     * numérico do sistema, pra somar itens sem sair do lançamento. DESLIGADO
     * por padrão — trocar o teclado numérico de todo mundo mexe demais no app;
     * quem quiser o de cálculo sempre liga aqui. Com ele desligado o campo
     * segue no teclado do sistema e ganha um ATALHO (ícone de calculadora ao
     * lado do valor) que abre o mesmo teclado sob demanda. Só vale pro
     * campo-herói do formulário; campos de valor dentro de sheets seguem no
     * teclado do sistema (não aninhamos sheets).
     */
    calculatorKeypad: boolean;
    /** Vibração (feedback háptico) no app todo. Ligado por padrão. */
    hapticsEnabled: boolean;
    /**
     * Vibração (bem leve, impacto soft) ao trocar de aba no menu inferior. Sub-opção de
     * hapticsEnabled — só tem efeito com a vibração geral ligada. DESLIGADO por padrão:
     * navegação não vibra de fábrica; quem gosta liga aqui.
     */
    tabHapticsEnabled: boolean;
    setCurrency: (c: CurrencyCode) => void;
    setDateFormat: (f: DateFormatOption) => void;
    setLanguage: (l: LanguagePreference) => void;
    setMonthStartDay: (day: number) => void;
    setSeparateCardPurchases: (on: boolean) => void;
    setOnlyConfirmedTransactions: (on: boolean) => void;
    setShowTagsInStatement: (on: boolean) => void;
    setShowNotesInStatement: (on: boolean) => void;
    setStatementCategoryLabel: (mode: StatementCategoryLabel) => void;
    setShowTransactionTime: (on: boolean) => void;
    setSearchInNotes: (on: boolean) => void;
    setShowPeriodBalance: (on: boolean) => void;
    setShowDailyRunningBalance: (on: boolean) => void;
    setRunningBalanceIncludesOpening: (on: boolean) => void;
    setIncludePendingInBalances: (on: boolean) => void;
    setShowReservaMovementsInStatement: (on: boolean) => void;
    setStatementSort: (sortBy: 'date' | 'amount' | 'category', sortDir: 'asc' | 'desc') => void;
    setAccountStatementSortDir: (sortDir: 'asc' | 'desc') => void;
    setShowPocketQuickAction: (on: boolean) => void;
    setPendingOnTop: (on: boolean) => void;
    setOverdueInCurrentMonth: (on: boolean) => void;
    setIncludeReceivablesInBalance: (on: boolean) => void;
    setStandaloneMonthlyForecast: (on: boolean) => void;
    setMonthResultInCurrentMonth: (on: boolean) => void;
    setInstallmentsFullValue: (on: boolean) => void;
    setInvoicesByClosingMonth: (on: boolean) => void;
    setReportsStatus: (status: ReportStatusFilter) => void;
    setCustomReportStatus: (status: ReportStatusFilter) => void;
    setShowAccountReserved: (on: boolean) => void;
    setCalculatorKeypad: (on: boolean) => void;
    setHapticsEnabled: (on: boolean) => void;
    setTabHapticsEnabled: (on: boolean) => void;
    /** Volta todas as preferências ao padrão (usado pelo "Apagar dados"). */
    reset: () => void;
    /** Volta só estas chaves ao padrão — o "Restaurar" da tela Fora do padrão, em Preferências. */
    restoreDefaults: (keys: readonly (keyof PreferencesState)[]) => void;
}

export const DEFAULT_PREFERENCES = {
    currency: 'BRL',
    dateFormat: 'dd/MM/yyyy',
    language: 'system',
    monthStartDay: CALENDAR_MONTH_START_DAY,
    separateCardPurchases: false,
    onlyConfirmedTransactions: false,
    showTagsInStatement: false,
    showNotesInStatement: false,
    statementCategoryLabel: DEFAULT_STATEMENT_CATEGORY_LABEL,
    showTransactionTime: true,
    searchInNotes: true,
    showPeriodBalance: false,
    showDailyRunningBalance: false,
    runningBalanceIncludesOpening: true,
    includePendingInBalances: true,
    // Ligado desde 27/09/2026: sem a linha, o Guardar derrubava o Disponível sem deixar rastro
    // no extrato e parecia "vir do nada" (e-mail do Denis). A linha é informativa, fora dos totais.
    showReservaMovementsInStatement: true,
    statementSortBy: 'date' as const,
    // Decrescente por padrão: extrato abre no dia mais recente, como extrato de banco.
    statementSortDir: 'desc' as const,
    accountStatementSortDir: 'asc' as const,
    showPocketQuickAction: false,
    pendingOnTop: true,
    overdueInCurrentMonth: false,
    includeReceivablesInBalance: false,
    standaloneMonthlyForecast: false,
    monthResultInCurrentMonth: false,
    installmentsFullValue: false,
    invoicesByClosingMonth: false,
    reportsStatus: DEFAULT_REPORT_STATUS,
    customReportStatus: 'settled' as const,
    showAccountReserved: true,
    calculatorKeypad: false,
    hapticsEnabled: true,
    tabHapticsEnabled: false,
} satisfies Partial<PreferencesState>;

export const usePreferencesStore = create<PreferencesState>()(
    persist(
        (set) => ({
            ...DEFAULT_PREFERENCES,
            setCurrency: (currency) => set({ currency }),
            setDateFormat: (dateFormat) => set({ dateFormat }),
            setLanguage: (language) => set({ language }),
            setMonthStartDay: (monthStartDay) => set({ monthStartDay: normalizeMonthStartDay(monthStartDay) }),
            setSeparateCardPurchases: (separateCardPurchases) => set({ separateCardPurchases }),
            setOnlyConfirmedTransactions: (onlyConfirmedTransactions) => set({ onlyConfirmedTransactions }),
            setShowTagsInStatement: (showTagsInStatement) => set({ showTagsInStatement }),
            setShowNotesInStatement: (showNotesInStatement) => set({ showNotesInStatement }),
            setStatementCategoryLabel: (statementCategoryLabel) => set({ statementCategoryLabel }),
            setShowTransactionTime: (showTransactionTime) => set({ showTransactionTime }),
            setSearchInNotes: (searchInNotes) => set({ searchInNotes }),
            setShowPeriodBalance: (showPeriodBalance) => set({ showPeriodBalance }),
            setShowDailyRunningBalance: (showDailyRunningBalance) => set({ showDailyRunningBalance }),
            setRunningBalanceIncludesOpening: (runningBalanceIncludesOpening) => set({ runningBalanceIncludesOpening }),
            setIncludePendingInBalances: (includePendingInBalances) => set({ includePendingInBalances }),
            setShowReservaMovementsInStatement: (showReservaMovementsInStatement) => set({ showReservaMovementsInStatement }),
            setStatementSort: (statementSortBy, statementSortDir) => set({ statementSortBy, statementSortDir }),
            setAccountStatementSortDir: (accountStatementSortDir) => set({ accountStatementSortDir }),
            setShowPocketQuickAction: (showPocketQuickAction) => set({ showPocketQuickAction }),
            setPendingOnTop: (pendingOnTop) => set({ pendingOnTop }),
            setOverdueInCurrentMonth: (overdueInCurrentMonth) => set({ overdueInCurrentMonth }),
            setIncludeReceivablesInBalance: (includeReceivablesInBalance) => set({ includeReceivablesInBalance }),
            setStandaloneMonthlyForecast: (standaloneMonthlyForecast) => set({ standaloneMonthlyForecast }),
            setMonthResultInCurrentMonth: (monthResultInCurrentMonth) => set({ monthResultInCurrentMonth }),
            setInstallmentsFullValue: (installmentsFullValue) => set({ installmentsFullValue }),
            setInvoicesByClosingMonth: (invoicesByClosingMonth) => set({ invoicesByClosingMonth }),
            setReportsStatus: (reportsStatus) => set({ reportsStatus }),
            setCustomReportStatus: (customReportStatus) => set({ customReportStatus }),
            setShowAccountReserved: (showAccountReserved) => set({ showAccountReserved }),
            setCalculatorKeypad: (calculatorKeypad) => set({ calculatorKeypad }),
            setHapticsEnabled: (hapticsEnabled) => set({ hapticsEnabled }),
            setTabHapticsEnabled: (tabHapticsEnabled) => set({ tabHapticsEnabled }),
            reset: () => set({ ...DEFAULT_PREFERENCES }),
            restoreDefaults: (keys) => {
                const defaults: Partial<PreferencesState> = DEFAULT_PREFERENCES;
                set(
                    Object.fromEntries(
                        keys.filter((key) => key in defaults).map((key) => [key, defaults[key]]),
                    ) as Partial<PreferencesState>,
                );
            },
        }),
        {
            name: PREFERENCES_STORAGE_KEY,
            storage: createJSONStorage(() => AsyncStorage),
            // v1: o padrão da ordenação do extrato virou decrescente. Quem está no
            // default antigo persistido (date/asc — indistinguível de "nunca mexeu")
            // migra pro novo padrão; qualquer outra combinação é escolha e fica.
            // v2: o teclado de cálculo virou opt-in. Como ele nunca chegou a uma
            // versão publicada, todo `calculatorKeypad` gravado veio do default
            // antigo (build de dev) — apagar a chave devolve o novo default.
            // v3: a hora nos lançamentos virou LIGADA por padrão. Como ela nasceu
            // desligada, `false` gravado é indistinguível de "nunca mexeu" (e é o
            // caso de praticamente toda instalação) — apagar a chave devolve o novo
            // default e liga pra todo mundo; quem não quiser desliga em Preferências.
            // v4: "Caixinhas no extrato" virou LIGADA por padrão, pelo mesmo raciocínio da
            // v3: nasceu desligada, então `false` gravado é "nunca mexeu" pra quase todos.
            version: 4,
            migrate: (persisted, version) => {
                const state = persisted as Partial<PreferencesState>;
                if (version < 1 && state.statementSortBy === 'date' && state.statementSortDir === 'asc') {
                    state.statementSortDir = 'desc';
                }
                if (version < 2) {
                    delete state.calculatorKeypad;
                }
                if (version < 3) {
                    delete state.showTransactionTime;
                }
                if (version < 4) {
                    delete state.showReservaMovementsInStatement;
                }
                return state;
            },
        },
    ),
);
